import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { Plus, Pencil, Trash2, Package, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';

const TIPOS_ARTICULO = [
  { value: 'SERVICIO', label: 'Servicio' },
  { value: 'PRODUCTO_ALMACENABLE', label: 'Producto Almacenable' },
  { value: 'PRODUCTO_NO_ALMACENABLE', label: 'Producto No Almacenable' }
];

const UNIDADES = ['UND', 'KG', 'M', 'LT', 'PAR', 'ROLLO', 'CAJA', 'BOLSA', 'PAQUETE', 'unidad', 'metro', 'kg'];

const ArticulosPage = () => {
  const { empresaActual } = useApp();
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [cuentasContables, setCuentasContables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Collapsible sections
  const [seccionBasica, setSeccionBasica] = useState(true);
  const [seccionInventario, setSeccionInventario] = useState(true);
  const [seccionCompras, setSeccionCompras] = useState(true);

  const initialFormData = {
    codigo: '',
    nombre: '',
    descripcion: '',
    categoria: 'Otros',
    tipo_articulo: 'PRODUCTO_ALMACENABLE',
    unidad_medida: 'UND',
    categoria_id: '',
    marca_id: '',
    imagen_url: '',
    stock_minimo: '',
    punto_reorden: '',
    cuenta_inventario_id: '',
    descripcion_compras: '',
    costo_compra: '',
    cuenta_gastos_id: '',
    incluye_impuesto: false,
    proveedor_id: '',
    control_por_rollos: false,
    activo: true
  };

  const [formData, setFormData] = useState(initialFormData);

  const loadData = useCallback(async () => {
    if (!empresaActual?.id) return;
    setLoading(true);
    try {
      // Cargar desde inventario unificado (prod_inventario)
      const [artRes, catRes, provRes, cuentasRes] = await Promise.all([
        api.get(`/articulos?empresa_id=${empresaActual.id}`),
        api.get(`/categorias?empresa_id=${empresaActual.id}`),
        api.get(`/proveedores?empresa_id=${empresaActual.id}`),
        api.get(`/cuentas-contables?empresa_id=${empresaActual.id}`)
      ]);
      setItems(artRes.data);
      setCategorias(catRes.data);
      setProveedores(provRes.data);
      setCuentasContables(cuentasRes.data);
      
      // Cargar marcas de producción
      try {
        const marcasRes = await api.get('/prod/marcas');
        setMarcas(marcasRes.data || []);
      } catch (e) {
        setMarcas([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar artículos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id]);

  useEffect(() => { loadData(); }, [loadData]);

  const resetForm = () => {
    setFormData(initialFormData);
    setEditingItem(null);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        codigo: item.codigo || '',
        nombre: item.nombre || '',
        descripcion: item.descripcion || '',
        categoria: item.categoria || 'Otros',
        tipo_articulo: item.tipo_articulo || 'PRODUCTO_ALMACENABLE',
        unidad_medida: item.unidad_medida || item.unidad || 'UND',
        categoria_id: item.categoria_id?.toString() || '',
        marca_id: item.marca_id || '',
        imagen_url: item.imagen_url || '',
        stock_minimo: item.stock_minimo?.toString() || '',
        punto_reorden: item.punto_reorden?.toString() || item.stock_minimo?.toString() || '',
        cuenta_inventario_id: item.cuenta_inventario_id?.toString() || '',
        descripcion_compras: item.descripcion_compras || '',
        costo_compra: item.costo_compra?.toString() || '',
        cuenta_gastos_id: item.cuenta_gastos_id?.toString() || '',
        incluye_impuesto: item.incluye_impuesto || false,
        proveedor_id: item.proveedor_id?.toString() || '',
        control_por_rollos: item.control_por_rollos || false,
        activo: item.activo !== false
      });
    } else {
      resetForm();
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nombre) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.codigo) {
      toast.error('El código es requerido');
      return;
    }

    const payload = {
      empresa_id: empresaActual.id,
      codigo: formData.codigo,
      nombre: formData.nombre,
      descripcion: formData.descripcion,
      categoria: formData.categoria,
      tipo_articulo: formData.tipo_articulo,
      unidad: formData.unidad_medida,
      unidad_medida: formData.unidad_medida,
      categoria_id: formData.categoria_id ? parseInt(formData.categoria_id) : null,
      marca_id: formData.marca_id || null,
      imagen_url: formData.imagen_url || null,
      stock_minimo: formData.stock_minimo ? parseInt(formData.stock_minimo) : 0,
      punto_reorden: formData.punto_reorden ? parseFloat(formData.punto_reorden) : 0,
      cuenta_inventario_id: formData.cuenta_inventario_id ? parseInt(formData.cuenta_inventario_id) : null,
      descripcion_compras: formData.descripcion_compras || null,
      costo_compra: formData.costo_compra ? parseFloat(formData.costo_compra) : 0,
      precio_ref: formData.costo_compra ? parseFloat(formData.costo_compra) : null,
      cuenta_gastos_id: formData.cuenta_gastos_id ? parseInt(formData.cuenta_gastos_id) : null,
      incluye_impuesto: formData.incluye_impuesto,
      proveedor_id: formData.proveedor_id ? parseInt(formData.proveedor_id) : null,
      control_por_rollos: formData.control_por_rollos,
      activo: formData.activo
    };

    try {
      if (editingItem) {
        await api.put(`/articulos/${editingItem.id}`, payload);
        toast.success('Artículo actualizado');
      } else {
        await api.post('/articulos', payload);
        toast.success('Artículo creado');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/articulos/${deleteConfirm.id}`);
      toast.success('Artículo eliminado');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const getTipoLabel = (tipo) => {
    const found = TIPOS_ARTICULO.find(t => t.value === tipo);
    return found ? found.label : tipo || 'Producto';
  };

  const getTipoBadgeColor = (tipo) => {
    switch (tipo) {
      case 'SERVICIO': return 'bg-blue-100 text-blue-800';
      case 'PRODUCTO_ALMACENABLE': return 'bg-green-100 text-green-800';
      case 'PRODUCTO_NO_ALMACENABLE': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const cuentasInventario = cuentasContables.filter(c => c.tipo === 'ACTIVO');
  const cuentasGastos = cuentasContables.filter(c => c.tipo === 'GASTO');

  if (!empresaActual) return <div className="text-center py-12 text-gray-500">Selecciona una empresa</div>;

  return (
    <div className="space-y-6" data-testid="articulos-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Inventario Unificado</h1>
            <p className="text-sm text-gray-500">Gestión de productos, servicios e inventario (Finanzas + Producción)</p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="new-articulo-btn">
          <Plus className="w-4 h-4 mr-2" />Nuevo Artículo
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No hay artículos registrados</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Unidad</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Costo</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono text-sm">{item.codigo || '-'}</TableCell>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {item.nombre}
                        {item.stock_actual <= (item.punto_reorden || item.stock_minimo || 0) && 
                         item.tipo_articulo === 'PRODUCTO_ALMACENABLE' && (
                          <AlertTriangle className="w-4 h-4 text-amber-500" title="Stock bajo" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getTipoBadgeColor(item.tipo_articulo)}>
                        {getTipoLabel(item.tipo_articulo)}
                      </Badge>
                    </TableCell>
                    <TableCell>{item.unidad_medida || item.unidad || 'UND'}</TableCell>
                    <TableCell>{item.categoria || item.categoria_fin_nombre || '-'}</TableCell>
                    <TableCell className="text-right">
                      {item.tipo_articulo === 'PRODUCTO_ALMACENABLE' || !item.tipo_articulo ? (
                        <span className={item.stock_actual <= (item.punto_reorden || item.stock_minimo || 0) ? 'text-red-600 font-semibold' : ''}>
                          {parseFloat(item.stock_actual || 0).toFixed(2)}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {item.costo_compra ? `S/. ${parseFloat(item.costo_compra).toFixed(2)}` : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(item)} data-testid={`edit-articulo-${item.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirm(item)} data-testid={`delete-articulo-${item.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Crear/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar' : 'Nuevo'} Artículo</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* Sección: Información Básica */}
            <Collapsible open={seccionBasica} onOpenChange={setSeccionBasica}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Información Básica</CardTitle>
                      {seccionBasica ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Código *</Label>
                        <Input 
                          value={formData.codigo} 
                          onChange={(e) => setFormData({ ...formData, codigo: e.target.value })}
                          placeholder="SKU o código interno"
                          required
                        />
                      </div>
                      <div>
                        <Label>Tipo de Artículo</Label>
                        <Select value={formData.tipo_articulo} onValueChange={(v) => setFormData({ ...formData, tipo_articulo: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {TIPOS_ARTICULO.map(t => (
                              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div>
                      <Label>Nombre *</Label>
                      <Input 
                        value={formData.nombre} 
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })} 
                        placeholder="Nombre del artículo"
                        required 
                      />
                    </div>

                    <div>
                      <Label>Descripción</Label>
                      <Textarea 
                        value={formData.descripcion} 
                        onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                        placeholder="Descripción del artículo"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Unidad de Medida</Label>
                        <Select value={formData.unidad_medida} onValueChange={(v) => setFormData({ ...formData, unidad_medida: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {UNIDADES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Categoría (texto)</Label>
                        <Input 
                          value={formData.categoria} 
                          onChange={(e) => setFormData({ ...formData, categoria: e.target.value })}
                          placeholder="Ej: Telas, Hilos, Avíos"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Categoría Finanzas</Label>
                        <Select value={formData.categoria_id || "none"} onValueChange={(v) => setFormData({ ...formData, categoria_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin categoría</SelectItem>
                            {categorias.map(c => <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div></div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Marca</Label>
                        <Select value={formData.marca_id || "none"} onValueChange={(v) => setFormData({ ...formData, marca_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar marca" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin marca</SelectItem>
                            {marcas.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center space-x-2 pt-6">
                        <Checkbox 
                          id="control_por_rollos"
                          checked={formData.control_por_rollos}
                          onCheckedChange={(checked) => setFormData({ ...formData, control_por_rollos: checked })}
                        />
                        <Label htmlFor="control_por_rollos" className="cursor-pointer">
                          Control por rollos (para telas)
                        </Label>
                      </div>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            {/* Sección: Información de Inventario (solo si es Producto Almacenable) */}
            {(formData.tipo_articulo === 'PRODUCTO_ALMACENABLE' || !formData.tipo_articulo) && (
              <Collapsible open={seccionInventario} onOpenChange={setSeccionInventario}>
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-gray-50 py-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base">Información del Inventario</CardTitle>
                        {seccionInventario ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Stock Mínimo (Punto de Reorden)</Label>
                          <Input 
                            type="number"
                            step="0.01"
                            value={formData.punto_reorden || formData.stock_minimo} 
                            onChange={(e) => setFormData({ ...formData, punto_reorden: e.target.value, stock_minimo: e.target.value })}
                            placeholder="0"
                          />
                          <p className="text-xs text-gray-500 mt-1">Alerta cuando el stock llegue a este nivel</p>
                        </div>
                        <div>
                          <Label>Cuenta de Activos de Inventario</Label>
                          <Select value={formData.cuenta_inventario_id || "none"} onValueChange={(v) => setFormData({ ...formData, cuenta_inventario_id: v === "none" ? "" : v })}>
                            <SelectTrigger><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin cuenta</SelectItem>
                              {cuentasInventario.map(c => (
                                <SelectItem key={c.id} value={c.id.toString()}>{c.codigo} - {c.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            )}

            {/* Sección: Compras */}
            <Collapsible open={seccionCompras} onOpenChange={setSeccionCompras}>
              <Card>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer hover:bg-gray-50 py-3">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Compras</CardTitle>
                      {seccionCompras ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-4">
                    <div>
                      <Label>Descripción de las Compras</Label>
                      <Textarea 
                        value={formData.descripcion_compras} 
                        onChange={(e) => setFormData({ ...formData, descripcion_compras: e.target.value })}
                        placeholder="Descripción que aparecerá en las órdenes de compra"
                        rows={2}
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Costo de Compra</Label>
                        <Input 
                          type="number"
                          step="0.01"
                          value={formData.costo_compra} 
                          onChange={(e) => setFormData({ ...formData, costo_compra: e.target.value })}
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <Label>Cuenta de Gastos</Label>
                        <Select value={formData.cuenta_gastos_id || "none"} onValueChange={(v) => setFormData({ ...formData, cuenta_gastos_id: v === "none" ? "" : v })}>
                          <SelectTrigger><SelectValue placeholder="Seleccionar cuenta" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Sin cuenta</SelectItem>
                            {cuentasGastos.map(c => (
                              <SelectItem key={c.id} value={c.id.toString()}>{c.codigo} - {c.nombre}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="incluye_impuesto"
                        checked={formData.incluye_impuesto}
                        onCheckedChange={(checked) => setFormData({ ...formData, incluye_impuesto: checked })}
                      />
                      <Label htmlFor="incluye_impuesto" className="cursor-pointer">
                        El costo incluye el impuesto sobre las compras
                      </Label>
                    </div>

                    <div>
                      <Label>Proveedor Preferido</Label>
                      <Select value={formData.proveedor_id || "none"} onValueChange={(v) => setFormData({ ...formData, proveedor_id: v === "none" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="Seleccionar proveedor" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin proveedor preferido</SelectItem>
                          {proveedores.map(p => (
                            <SelectItem key={p.id} value={p.id.toString()}>{p.razon_social || p.nro_doc}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Card>
            </Collapsible>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmación de Eliminación */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar artículo?</DialogTitle>
          </DialogHeader>
          <p className="text-gray-600">
            ¿Estás seguro de eliminar <strong>{deleteConfirm?.nombre}</strong>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArticulosPage;
